import PropTypes from 'prop-types';
import React, {
  createContext,
  useContext,
  useRef,
  useMemo,
  useCallback,
} from 'react';

const FieldArrayRegistryContext = createContext({
  register: () => {},
  unregister: () => {},
  get: () => undefined,
});

/**
 * FieldArrayRegistryProvider component to provide field array registry context.
 * @param {object} props - The props object.
 * @param {React.ReactNode} props.children - The children elements to be wrapped by the provider.
 * @returns {JSX.Element} The FieldArrayRegistryProvider component.
 */
export const FieldArrayRegistryProvider = ({ children }) => {
  const registryRef = useRef({});

  const register = useCallback((path, methods) => {
    registryRef.current[path] = methods;
  }, []);

  const unregister = useCallback((path) => {
    delete registryRef.current[path];
  }, []);

  const get = useCallback((path) => registryRef.current[path], []);

  const getAll = useCallback(() => registryRef.current, []);

  const value = useMemo(
    () => ({ register, unregister, get, getAll }),
    [register, unregister, get, getAll],
  );

  return (
    <FieldArrayRegistryContext.Provider value={value}>
      {children}
    </FieldArrayRegistryContext.Provider>
  );
};

FieldArrayRegistryProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * useFieldArrayRegistry hook to use the field array registry context.
 * @returns {object} The field array registry context value.
 */
export const useFieldArrayRegistry = () =>
  useContext(FieldArrayRegistryContext);
